import { Card, Center, Flex, Heading, Td, Tr } from "@chakra-ui/react";
import React, { useCallback, useState, useEffect } from "react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomInput from "../../components/CustomInput";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import store from "../../redux/store";
import { get_full_product_list } from "../../redux/productSlice";
import CustomToolTip from "../../components/CustomTooltip";
import { FiTrash } from "react-icons/fi";
import TableView from "../../components/TableView";
import { get_all_inventory_list } from "../../redux/inventorySlice";
import { baseUrl } from "../../utils/utils";

// // Validation Schema using Yup - Fixed to match initial values

// const AddProductSchema = Yup.object().shape({
//   product: Yup.string().required("Product selection is required"),

//   opening_stock: Yup.string()
//     .required("Opening stock is required")
//     .matches(/^\d+$/, "Opening stock must be a valid number")
//     .test(
//       "is-positive",
//       "Opening stock must be greater than 0",
//       (value) => Number(value) > 0
//     ),

//   current_stock: Yup.string()
//     .required("Current stock is required")
//     .matches(/^\d+$/, "Current stock must be a valid number")
//     .test(
//       "is-positive",
//       "Current stock must be greater than or equal to 0",
//       (value) => Number(value) >= 0
//     ),

//   opening_date: Yup.string().required("Opening date is required"),
// });

const AddProductSchema = Yup.object().shape({
  product: Yup.string().required("Product selection is required"),

  opening_stock: Yup.string()
    .required("Opening stock is required")
    .matches(/^\d+$/, "Opening stock must be a valid number")
    .test(
      "is-non-negative",
      "Opening stock must be greater than or equal to 0",
      (value) => value !== undefined && Number(value) >= 0
    ),

  current_stock: Yup.string()
    .required("Current stock is required")
    .matches(/^\d+$/, "Current stock must be a valid number")
    .test(
      "is-non-negative",
      "Current stock must be greater than or equal to 0",
      (value) => value !== undefined && Number(value) >= 0
    ),

  opening_date: Yup.string().required("Opening date is required"),
});

const AddInventory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitAction, setSubmitAction] = useState("submit"); // 'add' or 'submit'

  const { full_product_list } = useSelector((state) => state.products);
  const { all_inventory_list } = useSelector((state) => state.inventory);
  const [inventory_list, setInventory_list] = useState([]);
  const [loading, setLoading] = useState(false);

  const product_list = full_product_list?.map((val, ind) => ({
    value: val?.product_code,
    label: val.description + "-" + val?.category + "-" + val?.type,
  }));

  const filtered_product_list = product_list?.filter((product) => {
    const existingInventoryUUIDs =
      all_inventory_list?.map((item) => item.uuid || item.product_code) || [];

    // Get UUIDs from current inventory_list (local state)
    const currentInventoryUUIDs =
      inventory_list?.map((item) => item.product || item.uuid) || [];

    // Combine both arrays to get all used UUIDs
    const allUsedUUIDs = [...existingInventoryUUIDs, ...currentInventoryUUIDs];

    // Return true if product is not in any of the used UUIDs
    return !allUsedUUIDs.includes(product.value);
  });
  // Determine if we're in edit mode
  const isEditMode = Boolean(id) || location.pathname.includes("edit");
  const productData = location.state;
  console.log("location.state", location.state);

  const getInitialValues = () => {
    if (isEditMode && productData) {
      return {
        product_name: productData.product_name?.toString() || "",
        product: productData.uuid?.toString() || "",
        opening_stock: productData.os_fulls?.toString() || "",
        current_stock: productData.os_emptys?.toString() || "",
        opening_date: productData.opening_date || "",
      };
    }

    return {
      product_name: "",
      product: "",
      opening_stock: "",
      current_stock: "",
      opening_date: "",
    };
  };

  useEffect(() => {
    store.dispatch(get_full_product_list());
    store.dispatch(get_all_inventory_list());
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);

        const formData = new FormData();
        formData.append("action", isEditMode ? "update" : "create");

        // Add edit mode ID if available
        if (isEditMode && id) {
          formData.append("id", id);
          formData.append("uuid", values.product);
          formData.append("os_fulls", values.opening_stock);
          formData.append("os_emptys", values.current_stock);
          formData.append("opening_date", values.opening_date);
        } else {
          if (inventory_list.length > 0) {
            inventory_list.forEach((item, index) => {
              console.log(`Processing item ${index}:`, item); // Debug log

              const uuid = String(item.product || "");
              const openingStock = String(item.opening_stock || "");
              const currentStock = String(item.current_stock || "");
              const openingDate = String(item.opening_date || "");

              // Validation: Check if required fields are present
              if (!uuid) {
                throw new Error(`Product is required for item ${index + 1}`);
              }
              if (!openingStock) {
                throw new Error(
                  `Opening stock is required for item ${index + 1}`
                );
              }
              if (!currentStock) {
                throw new Error(
                  `Current stock is required for item ${index + 1}`
                );
              }
              if (!openingDate) {
                throw new Error(
                  `Opening date is required for item ${index + 1}`
                );
              }

              formData.append("uuid[]", uuid);
              formData.append("os_fulls[]", openingStock);
              formData.append("os_emptys[]", currentStock);
              formData.append("opening_date[]", openingDate);
            });
          } else {
            formData.append("uuid[]", values.product);
            formData.append("os_fulls[]", values.opening_stock);
            formData.append("os_emptys[]", values.current_stock);
            formData.append("opening_date[]", values.opening_date);
          }
        }

        const response = await fetch(baseUrl + "inventory.php", {
          method: "POST",
          body: formData,
        });
        const result = await response.json();

        // Fix: Better response checking
        if (response.ok && (result.success || result.status === "success")) {
          toast.success(result?.message);

          // Clear the list after successful submission (only for create mode)
          if (!isEditMode) {
            setInventory_list([]);
          }

          navigate(-1);
        } else {
          toast.error(result?.message);
          throw new Error(
            result.message || result.error || "An error occurred"
          );
        }
      } catch (error) {
        console.error("Inventory submission error:", error);

        if (
          error.message.toLowerCase().includes("duplicate") ||
          error.message.toLowerCase().includes("already exists")
        ) {
          toast.error("Some inventory items already exist");
        } else if (error.message.toLowerCase().includes("unauthorized")) {
          toast.error("Session expired. Please login again.");
          navigate("/login");
        } else {
          const errorMessage = isEditMode
            ? "Failed to update inventory. Please try again."
            : "Failed to add inventory. Please try again.";

          toast.error(error.message || errorMessage);
        }
      } finally {
        setLoading(false);
      }
    },
    [navigate, isEditMode, inventory_list, setInventory_list, id] // Added id dependency
  );
  if (isLoading) {
    return (
      <Card paddingInline={5} paddingBlock={10}>
        <Center>
          <Heading fontSize="lg">Loading inventory data...</Heading>
        </Center>
      </Card>
    );
  }

  const onAdd = async (values, { resetForm, setSubmitting }) => {
    try {
      setSubmitting(true);
      const newInventoryItem = {
        ...values,
      };
      setInventory_list((prevList) => [...prevList, newInventoryItem]);
      resetForm();
    } catch (error) {
      console.error("Error adding inventory:", error);
    } finally {
      setSubmitting(false);
    }
  };
  const handleRemoveInventory = (id) => {
    setInventory_list((prevList) => prevList.filter((item, ind) => ind !== id));
  };
  return (
    <>
      <Card
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
      >
        <Heading fontSize={"xl"}>
          {isEditMode ? "Edit Inventory" : "Add Inventory"}
        </Heading>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() => navigate(-1)}
        />
      </Card>
      {inventory_list?.length > 0 && (
        <Card display={"flex"} paddingInline={3} paddingBlock={5} gap={5}>
          <TableView
            headData={[
              "S.No.",
              "product_name",
              "os_fulls",
              "os_emptys",
              "opening_date",
              "Action",
            ]}
            body={inventory_list?.map((el, i) => (
              <Tr>
                <Td textAlign="center">{i + 1}</Td>
                <Td textAlign="center">{el.product}</Td>
                <Td textAlign="center">{el.opening_stock}</Td>
                <Td textAlign="center">{el.current_stock}</Td>
                <Td textAlign="center">{el.opening_date}</Td>
                <Td textAlign="center">
                  {
                    <Flex justify={"center"} gap={4}>
                      <CustomToolTip
                        button={
                          <FiTrash
                            size={20}
                            cursor={"pointer"}
                            color="#F00"
                            onClick={() => handleRemoveInventory(i)}
                          />
                        }
                        mess={"Delete Product"}
                      />
                    </Flex>
                  }
                </Td>
              </Tr>
            ))}
          />
          <Flex justifyContent={"flex-end"}>
            <CustomButton
              title={"Submit Inventory"}
              onClick={handleSubmit}
              loading={loading}
            />
          </Flex>
        </Card>
      )}

      {inventory_list?.length >= 4 || (
        <Formik
          key={isEditMode ? `edit-${id}` : "add"}
          initialValues={getInitialValues()}
          validationSchema={AddProductSchema}
          onSubmit={submitAction === "add" ? onAdd : handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting: formikSubmitting,
            isValid,
            dirty,
            submitForm,
          }) => (
            <Form>
              <Card padding={5}>
                {/* Row 1: Product Selection + Units */}
                {isEditMode ? (
                  <CustomInput
                    disabled1
                    label1={"Product"}
                    value1={values.product_name}
                    label2={"Opening Stock"}
                    value2={values.opening_stock}
                    onChange2={(e) => {
                      const value = e.target.value;

                      // Allow only digits (no letters, no decimals, no negative sign)
                      if (/^\d*$/.test(value)) {
                        setFieldValue("opening_stock", value);
                      }
                    }}
                    onBlur2={handleBlur("opening_stock")}
                    placeholder2={"Enter opening stock"}
                    error2={touched.opening_stock && errors.opening_stock}
                    required2={true}
                  />
                ) : (
                  <CustomInput
                    label1={"Product"}
                    value1={values.product}
                    onChange1={handleChange("product")}
                    onBlur1={handleBlur("product")}
                    select1={true}
                    options1={filtered_product_list}
                    placeholder1={"Select Product"}
                    error1={touched.product && errors.product}
                    required1={true}
                    label2={"Opening Stock"}
                    value2={values.opening_stock}
                    onChange2={(e) => {
                      const value = e.target.value;

                      // Allow only digits (no letters, no decimals, no negative sign)
                      if (/^\d*$/.test(value)) {
                        setFieldValue("opening_stock", value);
                      }
                    }}
                    onBlur2={handleBlur("opening_stock")}
                    placeholder2={"Enter opening stock"}
                    error2={touched.opening_stock && errors.opening_stock}
                    required2={true}
                  />
                )}

                {/* Row 2: Opening Stock + Current Stock */}
                <CustomInput
                  label1={"Opening Stock Empties"}
                  value1={values.current_stock}
                  onChange1={(e) => {
                    const value = e.target.value;

                    // Allow only digits (no letters, no decimals, no negative sign)
                    if (/^\d*$/.test(value)) {
                      setFieldValue("current_stock", value);
                    }
                  }}
                  onBlur1={handleBlur("current_stock")}
                  placeholder1={"Enter current stock"}
                  error1={touched.current_stock && errors.current_stock}
                  required1={true}
                  label2={"Opening Date"}
                  value2={values.opening_date}
                  onChange2={handleChange("opening_date")}
                  onBlur2={handleBlur("opening_date")}
                  placeholder2={"Select opening date"}
                  error2={touched.opening_date && errors.opening_date}
                  required2={true}
                  type2={"date"}
                />

                {/* Submit Button */}
                <Center marginBlock={5} gap={10}>
                  {isEditMode || (
                    <CustomButton
                      title={"Add Inventory"}
                      onClick={() => {
                        setSubmitAction("add"); // Set action to 'add'
                        setTimeout(() => submitForm(), 0); // Call submitForm after state update
                      }}
                      disabled={isSubmitting || formikSubmitting || !isValid}
                    />
                  )}
                  {inventory_list?.length > 0 || (
                    <CustomButton
                      title={isEditMode ? "Update" : "Submit"}
                      onClick={() => {
                        setSubmitAction("submit"); // Set action to 'submit'
                        setTimeout(() => submitForm(), 0); // Call submitForm after state update
                      }}
                      disabled={isSubmitting || formikSubmitting || !isValid}
                    />
                  )}
                </Center>
              </Card>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default AddInventory;
